import { useState } from "react";
import axios from "axios";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "soultrip_admin_token";

export const setAdminToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};
export const getAdminToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ""; }
  catch { return ""; }
};

function formatErr(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Already logged in? Redirect to dashboard.
  if (getAdminToken()) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      setAdminToken(r.data.access_token);
      navigate("/admin/dashboard", { replace: true });
    } catch (ex) {
      setError(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen w-screen flex items-center justify-center px-5 py-24"
      style={{ background: "var(--coal)" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 40% at 50% 30%, rgba(201,160,74,.08) 0%, transparent 70%)",
        }}
      />
      <div
        className="relative w-full max-w-md p-8 md:p-10"
        style={{
          background: "linear-gradient(160deg, rgba(20,16,10,.92) 0%, rgba(8,6,4,.96) 100%)",
          border: "1px solid rgba(201,160,74,.22)",
          boxShadow: "0 30px 80px rgba(0,0,0,.55)",
        }}
      >
        <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[var(--gold)] opacity-70" />
        <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[var(--gold)] opacity-70" />
        <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[var(--gold)] opacity-70" />
        <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[var(--gold)] opacity-70" />

        <div className="text-center">
          <span className="st-eyebrow">Admin · Mission Control</span>
          <h1 className="mt-3 st-cormorant text-3xl md:text-4xl italic font-light text-[var(--cream)]">
            Sign in
          </h1>
          <div className="st-rule mx-auto mt-4"/>
        </div>

        <form className="mt-8 space-y-5" onSubmit={onSubmit}>
          <label className="block">
            <div className="st-cinzel text-xs font-bold tracking-[0.28em] uppercase text-[var(--gold)]">Email</div>
            <div className="mt-2 relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gold-dk)]" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="w-full pl-9 pr-3 py-3 st-infant text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors"
                style={{ background: "rgba(5,4,3,.65)", border: "1px solid rgba(201,160,74,.25)" }}
                placeholder="admin@soultrip.show"
              />
            </div>
          </label>
          <label className="block">
            <div className="st-cinzel text-xs font-bold tracking-[0.28em] uppercase text-[var(--gold)]">Password</div>
            <div className="mt-2 relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gold-dk)]" />
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full pl-9 pr-3 py-3 st-infant text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors"
                style={{ background: "rgba(5,4,3,.65)", border: "1px solid rgba(201,160,74,.25)" }}
                placeholder="••••••••"
              />
            </div>
          </label>

          {error && (
            <div data-testid="admin-login-error" className="p-3 st-infant text-sm text-[#ff9a9a]"
                 style={{ border: "1px solid rgba(255,80,80,.45)", background: "rgba(255,80,80,.06)" }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full st-btn st-interactive inline-flex items-center justify-center gap-2"
            style={{ opacity: busy ? 0.6 : 1 }}
          >
            <LogIn size={13}/>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center st-cinzel text-[0.55rem] font-semibold tracking-[0.32em] uppercase text-[var(--gold-dk)]">
          Restricted area · authorized admins only
        </p>
      </div>
    </main>
  );
}
